import { api } from '@/shared/lib/api';
import type {
  OrdersResponse,
  OrdersQueryParams,
  OrderDetail,
  UpdateOrderStatusRequest,
  UpdateOrderStatusResponse,
  OrderHistoryResponse,
  OrderHistoryQueryParams,
  PurgeCompletedOrdersResponse,
  CancellationRequestListResponse,
  CancellationRequestDecisionResponse,
  CancellationRejectionReason,
  CustomerPolicy,
  UpdateCustomerPolicyRequest,
  AmendmentRequestListResponse,
  AmendmentDecisionResponse,
  AmendmentRejectionReason,
} from '../types/orders.types';

const ORDERS = '/orders';

/* ---- List orders (panel) ---- */

/** Fila do painel; `channel` (`menu` ou `whatsapp`) filtra pelo canal do pedido. */
export async function getOrders(
  params: OrdersQueryParams = {},
): Promise<OrdersResponse> {
  const { data } = await api.get<OrdersResponse>(ORDERS, { params });
  return data;
}

/* ---- Order detail (panel) ---- */

export async function getOrder(id: string): Promise<OrderDetail> {
  const { data } = await api.get<OrderDetail>(`${ORDERS}/${id}`);
  return data;
}

/* ---- Update order status ---- */

export async function updateOrderStatus(
  id: string,
  request: UpdateOrderStatusRequest,
): Promise<UpdateOrderStatusResponse> {
  const { data } = await api.patch<UpdateOrderStatusResponse>(
    `${ORDERS}/${id}/status`,
    request,
  );
  return data;
}

/* ---- Order history ---- */

/**
 * Histórico; `channel` combina com a busca, o período e o status. A resposta
 * traz `availableChannels`, que decide se o filtro "Canal" aparece.
 */
export async function getOrderHistory(
  params: OrderHistoryQueryParams = {},
): Promise<OrderHistoryResponse> {
  const { data } = await api.get<OrderHistoryResponse>(`${ORDERS}/history`, {
    params,
  });
  return data;
}

/* ---- Purge completed orders ---- */

export async function purgeCompletedOrders(): Promise<PurgeCompletedOrdersResponse> {
  const { data } = await api.post<PurgeCompletedOrdersResponse>(
    `${ORDERS}/completed/purge`,
  );
  return data;
}

/* ---- Solicitações de cancelamento do cliente (change orders-customer-cancellation) ---- */

/** As aguardando no prazo e as vencidas sem a equipe chamada, com o relógio do servidor. */
export async function getCancellationRequests(): Promise<CancellationRequestListResponse> {
  const { data } = await api.get<CancellationRequestListResponse>(`${ORDERS}/cancellation-requests`);
  return data;
}

export async function approveCancellationRequest(
  orderId: string,
  requestId: string,
): Promise<CancellationRequestDecisionResponse> {
  const { data } = await api.post<CancellationRequestDecisionResponse>(
    `${ORDERS}/${orderId}/cancellation-request/approve`,
    { requestId },
  );
  return data;
}

export async function rejectCancellationRequest(
  orderId: string,
  requestId: string,
  rejectionReason: CancellationRejectionReason,
): Promise<CancellationRequestDecisionResponse> {
  const { data } = await api.post<CancellationRequestDecisionResponse>(
    `${ORDERS}/${orderId}/cancellation-request/reject`,
    { requestId, rejectionReason },
  );
  return data;
}

/* ---- Alterações pedidas pelo cliente (change orders-customer-amendments) ---- */

/** As alterações que esperam a loja dentro do prazo, com o relógio do servidor. */
export async function getAmendmentRequests(): Promise<AmendmentRequestListResponse> {
  const { data } = await api.get<AmendmentRequestListResponse>(`${ORDERS}/amendment-requests`);
  return data;
}

/** Aprova com o total novo que a tela mostrou: outro total responde 409 `orders.total_changed`. */
export async function approveAmendment(
  orderId: string,
  amendmentId: string,
  expectedTotal: number,
): Promise<AmendmentDecisionResponse> {
  const { data } = await api.post<AmendmentDecisionResponse>(
    `${ORDERS}/${orderId}/amendments/${amendmentId}/approve`,
    { expectedTotal },
  );
  return data;
}

export async function rejectAmendment(
  orderId: string,
  amendmentId: string,
  reason: AmendmentRejectionReason,
): Promise<AmendmentDecisionResponse> {
  const { data } = await api.post<AmendmentDecisionResponse>(
    `${ORDERS}/${orderId}/amendments/${amendmentId}/reject`,
    { reason },
  );
  return data;
}

/* ---- Política do cliente: "Cancelar e alterar pelo WhatsApp" ---- */

export async function getCustomerPolicy(): Promise<CustomerPolicy> {
  const { data } = await api.get<CustomerPolicy>(`${ORDERS}/customer-policy`);
  return data;
}

export async function updateCustomerPolicy(request: UpdateCustomerPolicyRequest): Promise<CustomerPolicy> {
  const { data } = await api.put<CustomerPolicy>(`${ORDERS}/customer-policy`, request);
  return data;
}
